<script setup>
import dayjs from "dayjs";
import {computed} from "vue";
import {useAuthStore} from "../../store/auth.js";
import {useChatStore} from "../../store/chat.js";

const store = useChatStore()
const authStore = useAuthStore()
const props = defineProps({
    message: {
        type: Object,
        required: true
    }
})
const failed = computed(() => props?.message?.failed_at);
const isExternal = computed(() => props.message?.messageable_type?.includes("ChatContact"))
const name = computed(() => {
    switch (props.message?.messageable_type) {
        case 'App\\Models\\ChatContact':
            const client = props.message?.messageable?.client ?? null;
            return client ? client.first_name + " " + client.last_name : props.message?.messageable?.profile_name
        case "App\\Models\\AutoReply":
            return 'Auto Reply'
        default:
            const user = props.message.messageable;
            return user.first_name + " " + user.last_name;
    }
})

const scrollToParent = (id) => {

    const el = document.getElementById(
        "chat-message-container-" + id
    );

    if (el) {
        el.scrollIntoView({behavior: "smooth", block: "start"});
    }
};

const resend = (id) => {
    store.resendMessage(id)
}
</script>

<template>
    <div class="chat-message-container" :class="{'--is-internal': !isExternal, '--failed': failed}"
         :id="`chat-message-container-` + message.id">
        <button v-if="failed" class="chat-message-replay-button"
                @click="resend( message.id)"
                :class="{'--is-active': store?.reply_message?.id === message.id}">
            <ah-icon name="arrow-replay"/>
        </button>
        <div class="chat-message">

            <div class="chat-message-author">
                <p>{{ name }}</p>
            </div>

            <!--            If response has an attachement and text response should just show text,-->
            <!--            If the response is just a attachement just show the file no preview needed.-->
            <!--            Do scroll ups on replys-->
            <div class="chat-message-reply" v-if="message.parent" @click="scrollToParent(message.parent.id)">
                <ah-icon name="arrow-reply" color="var(--color-tiffany-dark)"/>
                <div class="">
                    <div class="chat-message-author">
                        <p>{{
                                message?.parent?.messageable?.first_name ?? message?.parent?.messageable?.client?.first_name
                            }}
                            {{
                                message?.parent?.messageable?.last_name ?? message?.parent?.messageable?.client?.last_name
                            }}</p>
                    </div>
                    <p>{{ message.parent.content }}</p>
                </div>
            </div>

            <div class="chat-message-attachments" v-if="message.attachments && message.attachments.length > 0">
                <div class="chat-message-attachment" v-for="a in message.attachments">
                    <div class="chat-message-attachment-details">
                        <ah-icon name="paper-file" color="var(--color-tiffany-dark)"/>

                        <div class="chat-message-attachment-details-info">
                            <p>{{ a.file_name }}</p>
                            <span>{{ a.mime_type }}  •  {{ (a.bytes / 1024 / 1024).toFixed(2) }}MB</span>
                        </div>
                    </div>
                    <div class="chat-message-attachment-actions">
                        <img :src="a.url" alt="" v-if="a.mime_type.includes('image')">
                        <audio :src="a.url" controls v-else-if="a.mime_type.includes('audio')" class="chat-message-audio"/>
                        <a :href="a.url" :download="a.file_name" target="_blank">
                            <ah-icon name="arrow-in"/>
                        </a>
                    </div>
                </div>
            </div>

            <div class="chat-message-content" v-html="message.content"></div>
            <div class="chat-message-meta">
                <p>{{ dayjs(message.created_at).format('DD MMMM YYYY') }}</p>
                <p>{{ dayjs(message.created_at).format('HH:mm') }}</p>

                <ah-icon :name="failed ? 'exclaim-circle' : message?.read_at ? 'check-all' : 'check-single'"
                         :color="failed ? 'var(--color-lemon)' : message?.read_at ? 'var(--color-tiffany)' : 'var(--color-hazy)'"
                         v-if="!isExternal"/>
            </div>
        </div>
        <button v-if="isExternal" class="chat-message-reply-button"
                :class="{'--is-active': store?.reply_message?.id === message.id}"
                @click="store.setReplyMessage(message)">
            <ah-icon name="arrow-reply"/>
        </button>
    </div>
</template>

<style lang="scss">

.chat-message-container {
    display: flex;
    gap: 1rem;
    width: 100%;
    align-items: start;

    .chat-message {
        padding: 2rem;
        background: #fff;
        border-radius: 0 2.4rem 2.4rem 2.4rem;
        display: flex;
        flex-direction: column;
        gap: 2rem;
        width: 80%;

        .chat-message-reply {
            padding: 2rem;
            border-radius: 2.4rem;
            background: var(--color-dew);
            display: flex;
            gap: 2rem;
            cursor: pointer;

            @include dark() {
                background: var(--color-black-40);

                .chat-message-author {
                    p {
                        color: var(--color-tiffany-dark);
                    }
                }
            }
        }

        .chat-message-author {
            p {
                @include font(1.4, null, black);
            }
        }

        .chat-message-content {
            width: 80%;
            overflow-wrap: anywhere;
            word-break: break-word;
        }

        .chat-message-meta {
            display: flex;
            align-self: end;
            gap: 1rem;

            p {
                @include font(1, 2.2, black);
                background: var(--color-grey-mist);
                padding: 0 1rem;
                color: #0A332C;
                border-radius: 1.1rem;
            }
        }

        &-attachments {
            display: flex;
            flex-direction: column;
            gap: 1rem;

            .chat-message-attachment {
                min-height: 8rem;
                display: flex;
                align-items: center;
                justify-content: space-between;
                background: var(--color-dew);
                border-radius: 2.4rem;
                padding: 1rem 2rem;

                &-details {
                    display: flex;
                    align-items: center;
                    gap: 2rem;

                    &-info {
                        display: flex;
                        flex-direction: column;
                        gap: 1rem;

                        p {
                            @include font(1.4, 1.4, black);
                            color: var(--color-tiffany-dark);
                        }

                        span {
                            @include font(1.2, 1.4, book);
                            text-transform: uppercase;
                        }
                    }
                }

                &-actions {
                    display: flex;
                    gap: 1rem;
                    align-items: center;

                    img {
                        height: 8.9rem;
                        border-radius: 1.2rem;
                    }

                    .chat-message-audio {
                        height: 4rem;
                        border-radius: 2rem;
                        max-width: 24rem;
                    }

                    button, a {
                        width: 4.8rem;
                        height: 4.8rem;
                        border-radius: 50%;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        border: none;
                        background: var(--color-tiffany-dark);

                        .icon {
                            fill: var(--color-white);
                        }
                    }
                }
            }

            @include dark() {
                .chat-message-attachment {
                    background: var(--color-black-40);

                    &-details {
                        &-info {
                            span {
                                color: var(--color-white);
                            }
                        }
                    }
                }
            }
        }
    }

    .chat-message-reply-button {
        width: 3.6rem;
        height: 3.6rem;
        background: var(--color-white);
        border-radius: 50%;
        box-shadow: none;
        border: none;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        margin-top: 1rem;
        transition: .3s ease-in-out;

        .icon {
            fill: var(--color-hazy);
            transition: .3s ease-in-out;
        }

        &:hover, &.--is-active {
            background: var(--color-raspberry) !important;

            .icon {
                fill: var(--color-white);
            }
        }

        @include dark() {
            background: var(--color-darth-maul);
        }
    }

    .chat-message-replay-button {
        width: 3.6rem;
        height: 3.6rem;
        background: var(--color-orange);
        border-radius: 50%;
        box-shadow: none;
        border: none;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        margin-top: 1rem;
        transition: .3s ease-in-out;

        .icon {
            fill: var(--color-white);
            transition: .3s ease-in-out;
        }

        &:hover {
            background: var(--color-raspberry) !important;
        }
    }

    &.--is-internal {
        justify-content: flex-end;

        .chat-message {
            border-radius: 2.4rem 0 2.4rem 2.4rem;
            background: #006661 !important;


            .chat-message-author {
                p {
                    color: var(--color-theme-primary) !important;
                }
            }

            .chat-message-content {
                max-width: 80%;
                color: #fff;
            }

            .chat-message-meta {
                p {
                    background: var(--color-white-60);
                }
            }

            .chat-message-reply {
                background: var(--color-black-40);

                p {
                    color: var(--color-white);
                }
            }
        }

        &.--failed {
            .chat-message {
                background: var(--color-orange) !important;

                .chat-message-author {
                    p {
                        color: var(--color-lemon) !important;
                    }
                }

                .chat-message-meta {
                    p {
                        background: var(--color-lemon) !important;
                        color: var(--color-tamarisk) !important;
                    }
                }
            }
        }
    }

    @include dark() {
        .chat-message {
            background: var(--color-darth-maul);

            .chat-message-content {
                color: var(--color-white);
            }

            .chat-message-meta {
                p {
                    background: var(--color-white-60);
                    color: var(--color-tamarisk);
                }
            }
        }
    }
}
</style>
